import { Dialog } from '@headlessui/react';
import { Button, Icon } from '../../ui';
import type { SubmissionNoteControl } from './useSubmissionNote';

/* Submission notes: a job working client feedback (its ai_job_id is on a
   client_feedback_batch) posts its approval client note onto the client's
   feedback group, where their app shows it "From Bullfinch". The job page gets
   a Submit Note button under the client note, and Submitted asks first when
   the note hasn't been posted (or was edited since). Every other job: nothing. */

const timeOf = (iso: string) => {
  const d = new Date(iso);
  const sameDay = d.toDateString() === new Date().toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Under the client note on a feedback job: Submit Note, then "Note submitted · 5:52 AM" with Submit again. */
export function SubmitNoteBar({ control }: { control: SubmissionNoteControl }) {
  const { state, posting, error, appLabel } = control;
  if (state.kind === 'none') return null;
  const groups = control.batches.filter((b) => b.status !== 'withdrawn').length;
  const where = `${appLabel}${groups > 1 ? ` (${groups} groups)` : ''}`;
  return (
    <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {state.kind === 'missing' ? (
          <>
            <Button size="sm" icon="send" disabled={posting} onClick={() => void control.post()}>
              {posting ? 'Submitting…' : 'Submit Note'}
            </Button>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>
              Posts this note on the client’s feedback in {where}.
            </span>
          </>
        ) : (
          <>
            <span
              className="ck-menu-pop"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: 'var(--text-sm)', fontWeight: 500,
                color: state.kind === 'posted' ? 'var(--status-up)' : 'var(--status-warn)', transformOrigin: 'left center',
              }}
            >
              <Icon name={state.kind === 'posted' ? 'check-circle-2' : 'triangle-alert'} size={14} />
              Note submitted · {timeOf(state.at)}
            </span>
            {state.kind === 'changed' ? (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-warn)' }}>Edited since: the client still sees the earlier text.</span>
            ) : null}
            <Button size="sm" variant={state.kind === 'changed' ? 'primary' : 'ghost'} icon="rotate-ccw" disabled={posting} onClick={() => void control.post()}>
              {posting ? 'Submitting…' : 'Submit again'}
            </Button>
          </>
        )}
      </div>
      {state.kind !== 'missing' ? (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}>Submit again posts the note as it reads now and replaces the one on {where}.</span>
      ) : null}
      {error ? <span style={{ fontSize: 'var(--text-xs)', color: 'var(--status-down)' }}>{error}</span> : null}
    </div>
  );
}

/**
 * Submitted, but the client note isn't on the client's feedback (or was edited
 * since): ask. Same frame as Cockpit's confirm dialog (DeleteConfirmationModal).
 */
export function NoteNotSubmittedDialog({
  open,
  control,
  busy,
  onClose,
  onSubmitted,
  zIndex = 50,
}: {
  open: boolean;
  control: SubmissionNoteControl;
  /** Marking submitted is under way. */
  busy: boolean;
  onClose: () => void;
  /** Mark the job submitted (after the note posts, or without it). */
  onSubmitted: () => Promise<void> | void;
  zIndex?: number;
}) {
  const working = busy || control.posting;
  const changed = control.state.kind === 'changed';
  const submitBoth = async () => {
    if (await control.post()) await onSubmitted();
  };
  return (
    <Dialog open={open} onClose={() => !working && onClose()} className="relative" style={{ zIndex }}>
      <div className="fixed inset-0" style={{ background: 'var(--surface-overlay)' }} aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 ck-safe-pad">
        <Dialog.Panel
          role="alertdialog"
          className="mx-auto max-w-md w-full ck-menu-pop"
          style={{
            background: 'var(--surface-card)',
            border: '1px solid var(--border-hairline)',
            borderRadius: 'var(--radius-xl)',
            boxShadow: 'var(--shadow-pop)',
            padding: 'var(--space-6)',
          }}
        >
          <Dialog.Title
            style={{
              display: 'flex', alignItems: 'center', gap: '11px', margin: '0 0 12px',
              fontSize: 'var(--text-md)', fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
              letterSpacing: 'var(--tracking-tight)', color: 'var(--text-body)',
            }}
          >
            <Icon name="triangle-alert" size={16} style={{ color: 'var(--status-warn)' }} />
            You haven’t submitted the client note
          </Dialog.Title>
          <p style={{ margin: '0 0 22px', fontSize: 'var(--text-sm)', color: 'var(--text-muted)', lineHeight: 1.5 }}>
            {changed
              ? `You edited the client note after submitting it. The client’s feedback in ${control.appLabel} still shows the earlier text.`
              : `The client note hasn’t been posted to the client’s feedback in ${control.appLabel}, so they won’t see it on their notes.`}
          </p>
          {control.error ? (
            <p style={{ margin: '-10px 0 16px', fontSize: 'var(--text-sm)', color: 'var(--status-down)' }}>{control.error}</p>
          ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Button icon="send" disabled={working} onClick={() => void submitBoth()}>
              {control.posting ? 'Submitting the note…' : busy ? 'Marking submitted…' : 'Submit note and mark submitted'}
            </Button>
            <Button variant="secondary" disabled={working} onClick={() => void onSubmitted()}>
              Mark submitted without the note
            </Button>
            <Button variant="ghost" disabled={working} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
